/**
 * Working Calendar — generic working-day arithmetic (Step 4).
 *
 * Mirrors backend/src/calendars/calendar-engine.ts. This app has no shared package
 * between the CRA frontend and the Nest backend, so both runtimes carry their own copy
 * of this math — the frontend needs it for live Gantt cascade/critical-path without a
 * round-trip per edit, the backend for AI schedule assembly + EVM PV. Keep the two in
 * lockstep — same function names, same semantics, same tests on both sides. Holiday
 * RULE->DATE expansion (Easter, nth-weekday, observance shifts) is NOT duplicated here:
 * that stays backend-only, and this module only ever consumes its resolved output
 * (a plain set of YYYY-MM-DD strings already fetched from the calendar's data).
 */
import { CalendarException, CalendarWeek } from '../types';

export interface ResolvedCalendar {
  week: CalendarWeek;
  exceptions: CalendarException[];
  /** Already resolved actual dates (YYYY-MM-DD), with any disabled holiday names filtered out. */
  holidayDates: Set<string>;
}

const fmtISO = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseISO = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addCalDays = (d: Date, n: number): Date => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

function exceptionMatchesDate(exc: CalendarException, date: Date): boolean {
  const ds = fmtISO(date);
  if (exc.repeat === 'once') {
    return ds >= exc.from_date && ds <= exc.to_date;
  }
  const from = parseISO(exc.from_date);
  const to = parseISO(exc.to_date);
  if (exc.repeat === 'weekly') {
    if (date < from || date > to) return false;
    return date.getDay() === from.getDay();
  }
  // yearly: month-day within [from, to] range, applied every year (wraps across Dec->Jan)
  const mmdd = (d: Date) => (d.getMonth() + 1) * 100 + d.getDate();
  const dmd = mmdd(date), fmd = mmdd(from), tmd = mmdd(to);
  return fmd <= tmd ? (dmd >= fmd && dmd <= tmd) : (dmd >= fmd || dmd <= tmd);
}

export function findMatchingException(cal: ResolvedCalendar, date: Date): CalendarException | undefined {
  return cal.exceptions.find(exc => exceptionMatchesDate(exc, date));
}

/** isWorkingDay order: 1) matching exception wins · 2) active holiday -> non-working · 3) work-week ranges. */
export function isWorkingDay(cal: ResolvedCalendar, date: Date): boolean {
  const exc = findMatchingException(cal, date);
  if (exc) return exc.type === 'work';
  if (cal.holidayDates.has(fmtISO(date))) return false;
  const weekIndex = (date.getDay() + 6) % 7; // Mon=0..Sun=6
  return (cal.week[weekIndex] || []).length > 0;
}

export function nextWorkingDay(cal: ResolvedCalendar, date: Date): Date {
  let d = new Date(date);
  while (!isWorkingDay(cal, d)) d = addCalDays(d, 1);
  return d;
}

/** Inclusive: `start` itself counts as day 1 if it's a working day. */
export function addWorkingDays(cal: ResolvedCalendar, start: Date, workingDays: number): Date {
  let cur = new Date(start);
  let rem = workingDays;
  while (rem > 0) {
    if (isWorkingDay(cal, cur)) rem--;
    if (rem > 0) cur = addCalDays(cur, 1);
  }
  return cur;
}

/** Inclusive: `from` itself counts as day 1 if it's a working day. */
export function subtractWorkingDays(cal: ResolvedCalendar, from: Date, workingDays: number): Date {
  let cur = new Date(from);
  let rem = workingDays;
  while (rem > 0) {
    if (isWorkingDay(cal, cur)) rem--;
    if (rem > 0) cur = addCalDays(cur, -1);
  }
  return cur;
}

/** Inclusive count of working days in [from, to]. No forced minimum — callers apply Math.max(1, ...) if needed. */
export function workingDaysBetween(cal: ResolvedCalendar, from: Date, to: Date): number {
  let count = 0;
  let cur = new Date(from);
  while (cur <= to) {
    if (isWorkingDay(cal, cur)) count++;
    cur = addCalDays(cur, 1);
  }
  return count;
}
